import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-11987">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-11988">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-11989">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-11990">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-11991">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-11992">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-11993">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-11994">
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/* Compact result badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-bold text-sm
          ${isInKofschip ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-orange-50 border-orange-300 text-orange-800'}`} data-id="element-11995">
          <span className="font-black text-base" data-id="element-11996">{stem}</span>
          <span className="opacity-60" data-id="element-11997">+</span>
          <span className="font-black text-base" data-id="element-11998">{useTE ? '-te' : '-de'}</span>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-11999">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-12000">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-12001">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-12002">
            <span className="text-2xl" data-id="element-12003">⛵</span>
            <div className="text-left" data-id="element-12004">
              <div className="text-sm font-bold text-blue-900" data-id="element-12005">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-12006">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Ezelsbruggetje (Tip) */}
        <div className="mb-6 bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-12007">
          <div className="flex items-start gap-3" data-id="element-12008">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-12009">
              <span className="text-amber-600 text-lg" data-id="element-12010">💡</span>
            </div>
            <div data-id="element-12011">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-12012">
                Ezelsbruggetje
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12013">
                Zit de laatste letter van de stam in{' '}
                <strong data-id="element-12014">'t KOFSCHIP</strong>?
                <br data-id="element-12015" />✅ Ja → <strong data-id="element-12016">-te(n)</strong>
                <br data-id="element-12017" />❌ Nee → <strong data-id="element-12018">-de(n)</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Stap-voor-stap voorbeeld (Compact) */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-12019">
          <div className="flex items-start gap-3" data-id="element-12020">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12021">
              <span className="text-emerald-600 text-lg" data-id="element-12022">📝</span>
            </div>
            <div className="w-full" data-id="element-12023">
              <h4 className="text-sm font-bold text-emerald-900 mb-4" data-id="element-12024">
                Voorbeeld: {verb}
              </h4>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-12025">
                <div className="text-center" data-id="element-12026">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12027">Stam</div>
                  <div className="font-bold text-lg text-blue-700" data-id="element-12028">{stem}</div>
                </div>
                <div className="text-slate-400" data-id="element-12029">→</div>
                <div className="text-center" data-id="element-12030">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12031">
                    Laatste letter
                  </div>
                  <div className={`font-bold text-xl ${isInKofschip ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-12032">
                    {lastLetter.toUpperCase()}
                  </div>
                </div>
                <div className="text-slate-400" data-id="element-12033">→</div>
                <div className="text-center" data-id="element-12034">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12035">
                    In 't KOFSCHIP?
                  </div>
                  <div className="font-bold text-lg" data-id="element-12036">
                    {isInKofschip ? '✅ Ja' : '❌ Nee'}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center p-3 rounded-lg border-2 font-bold bg-white shadow-sm" data-id="element-12037">
                Dus: <span className="text-emerald-700" data-id="element-12038">{stem}</span> +{' '}
                <span className="text-emerald-700" data-id="element-12039">
                  {useTE ? 'te(n)' : 'de(n)'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Steps */}
        <details className="group bg-white/60 rounded-xl border border-blue-100 shadow-sm overflow-hidden" data-id="element-12040">
          <summary className="cursor-pointer p-4 font-semibold text-blue-800 hover:bg-blue-50/50 transition-colors flex items-center justify-between" data-id="element-12041">
            <span data-id="element-12042">Meer uitleg (stap voor stap)</span>
            <span className="text-blue-400 group-open:rotate-180 transition-transform" data-id="element-12043">
              ▼
            </span>
          </summary>
          <div className="p-5 border-t border-blue-100 space-y-6" data-id="element-12044">
            {/* Step 1: Verb → Stem */}
            <div className="flex items-start gap-3" data-id="element-12045">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12046">
                <span className="text-blue-600 font-bold text-xs" data-id="element-12047">1</span>
              </div>
              <div data-id="element-12048">
                <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-12049">
                  Vind de stam
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12050">
                  Haal -en van het hele werkwoord af: <strong data-id="element-12051">{verb}</strong> →{' '}
                  <strong data-id="element-12052">{stem}</strong>
                </p>
              </div>
            </div>

            {/* Step 2: Last Letter Check */}
            <div className="flex items-start gap-3" data-id="element-12053">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12054">
                <span className="text-cyan-600 font-bold text-xs" data-id="element-12055">2</span>
              </div>
              <div data-id="element-12056">
                <h4 className="text-sm font-bold text-cyan-900 mb-1" data-id="element-12057">
                  Kijk naar de laatste letter
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12058">
                  De laatste letter van de stam is de{' '}
                  <strong data-id="element-12059">{lastLetter.toUpperCase()}</strong>.
                </p>
              </div>
            </div>

            {/* Step 3: KOFSCHIP Check */}
            <div className="flex items-start gap-3" data-id="element-12060">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12061">
                <span className="text-indigo-600 font-bold text-xs" data-id="element-12062">3</span>
              </div>
              <div className="w-full" data-id="element-12063">
                <h4 className="text-sm font-bold text-indigo-900 mb-3" data-id="element-12064">
                  Zit deze in 't KOFSCHIP?
                </h4>

                <div className="flex justify-start gap-1.5 mb-3 flex-wrap" data-id="element-12065">
                  <div className="text-lg font-black text-slate-400 mr-1" data-id="element-12066">
                    't
                  </div>
                  {kofschipLetters.map(letter => {
                  const isMatch = letter === lastLetter.toUpperCase();
                  return <div key={letter} className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm shadow-sm
                          ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-12067">
                        {letter}
                      </div>;
                })}
                </div>

                {!isInKofschip && <div className="text-sm text-rose-600 font-semibold mb-2" data-id="element-12068">
                    De {lastLetter.toUpperCase()} zit er NIET in.
                  </div>}
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>;
}